import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, Send } from 'lucide-react';
import { formatCurrency } from '@/lib/format';
import i18n from '@/lib/i18n';
import {
  usePlatformConnect,
  useResendInvite,
  type PlatformConnectStatus,
} from '@/api/hooks/usePlatformConnect';
import type { AppError } from '@/api/client';

/**
 * Cross-tenant Stripe Connect dashboard. Read-only except for the
 * "Resend invite" affordance for operators who still have a pending
 * invite — same pattern as the tenants table (single scrollable
 * list with inline actions).
 */
export default function PlatformConnectPage() {
  const { data, isLoading, isError } = usePlatformConnect();
  const resend = useResendInvite();
  const [pendingTenantId, setPendingTenantId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const rows = data ?? [];
    const sum = rows.reduce(
      (acc, r) => {
        acc.revenue += Number(r.totalRevenue ?? 0);
        acc.fees += Number(r.totalPlatformFees ?? 0);
        if (r.connectStatus === 'CONNECTED') acc.connected++;
        else if (r.connectStatus === 'PENDING') acc.pending++;
        else acc.notStarted++;
        return acc;
      },
      { connected: 0, pending: 0, notStarted: 0, revenue: 0, fees: 0 },
    );
    return sum;
  }, [data]);

  const handleResend = (row: PlatformConnectStatus) => {
    if (!row.hasInvitePending) {
      toast.info('This operator has already activated their account.');
      return;
    }
    setPendingTenantId(row.tenantId);
    resend.mutate(row.tenantId, {
      onSuccess: (res) => {
        if (res.status === 'RESENT') {
          toast.success(`Invite resent to ${res.email ?? row.name}`);
        } else {
          toast.info(res.message);
        }
      },
      onError: (err) => {
        const e = err as unknown as AppError;
        toast.error(e?.message ?? 'Failed to resend invite');
      },
      onSettled: () => setPendingTenantId(null),
    });
  };

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400">
          Platform Admin
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
          Stripe Connect
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Payment infrastructure status across all operators.
        </p>
      </header>

      <section className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Connected" value={counts.connected} tone="emerald" />
        <SummaryCard label="Pending" value={counts.pending} tone="amber" />
        <SummaryCard label="Not started" value={counts.notStarted} tone="neutral" />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricCard label="Total GBV" value={formatCurrency(counts.revenue, 'CAD', i18n.language)} />
        <MetricCard
          label="Platform fees collected"
          value={formatCurrency(counts.fees, 'CAD', i18n.language)}
        />
      </section>

      <div className="mt-6 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-4 py-3 text-left">Operator</th>
                <th scope="col" className="px-4 py-3 text-left">Status</th>
                <th scope="col" className="px-4 py-3 text-left">Stripe Account</th>
                <th scope="col" className="px-4 py-3 text-center">Charges</th>
                <th scope="col" className="px-4 py-3 text-center">Payouts</th>
                <th scope="col" className="px-4 py-3 text-right">Revenue</th>
                <th scope="col" className="px-4 py-3 text-right">Platform Fees</th>
                <th scope="col" className="px-4 py-3 text-right">Fee %</th>
                <th scope="col" className="px-4 py-3 text-left">Last Payment</th>
                <th scope="col" className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-neutral-400">
                    Loading operators…
                  </td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-red-600">
                    Failed to load Connect statuses.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && (data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-neutral-400">
                    No operators yet.
                  </td>
                </tr>
              )}
              {data?.map((row) => (
                <tr key={row.tenantId} className="hover:bg-neutral-50">
                  <td className="whitespace-nowrap px-4 py-3">
                    <p className="font-medium text-neutral-900">{row.name}</p>
                    <p className="font-mono text-xs text-neutral-400">{row.slug}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StripeBadge status={row.connectStatus} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-neutral-500">
                    {row.stripeAccountId ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <YesNo value={row.chargesEnabled} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <YesNo value={row.payoutsEnabled} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-900">
                    {formatCurrency(Number(row.totalRevenue ?? 0), 'CAD', i18n.language)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-900">
                    {formatCurrency(Number(row.totalPlatformFees ?? 0), 'CAD', i18n.language)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-neutral-700">
                    {row.platformFeePercent != null ? `${Number(row.platformFeePercent).toFixed(2)}%` : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                    {row.lastPaymentAt ? (
                      <RelativeDate iso={row.lastPaymentAt} />
                    ) : (
                      <span className="text-neutral-400">No payments</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <RowActions
                      row={row}
                      busy={pendingTenantId === row.tenantId && resend.isPending}
                      onResend={() => handleResend(row)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RowActions({
  row,
  busy,
  onResend,
}: {
  row: PlatformConnectStatus;
  busy: boolean;
  onResend: () => void;
}) {
  const btnCls =
    'inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="flex justify-end gap-2">
      <Link
        to={`/platform/tenants/${row.tenantId}`}
        className={btnCls}
      >
        View tenant
      </Link>
      {row.connectStatus === 'CONNECTED' && row.stripeAccountId && (
        <a
          href={`https://dashboard.stripe.com/connect/accounts/${row.stripeAccountId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={btnCls}
        >
          Stripe <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
      {row.hasInvitePending && (
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          className={btnCls + ' text-[#C2713A] hover:bg-[#C2713A]/5'}
        >
          <Send className="h-3 w-3" aria-hidden="true" />
          {busy ? 'Sending…' : 'Resend invite'}
        </button>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'emerald' | 'amber' | 'neutral';
}) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    neutral: 'border-neutral-200 bg-neutral-50 text-neutral-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <p className="text-xs font-medium uppercase tracking-wider opacity-75">{label}</p>
      <p className="mt-1 font-mono text-2xl font-semibold">{value}</p>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wider text-neutral-400">{label}</p>
      <p className="mt-1 font-mono text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function StripeBadge({ status }: { status: PlatformConnectStatus['connectStatus'] }) {
  const styles: Record<string, { label: string; cls: string }> = {
    CONNECTED: { label: 'Connected', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    PENDING: { label: 'Onboarding', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    NOT_STARTED: { label: 'Not started', cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
  };
  const entry = styles[status] ?? { label: status, cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${entry.cls}`}>
      {entry.label}
    </span>
  );
}

function YesNo({ value }: { value: boolean }) {
  return value ? (
    <span className="font-mono text-emerald-600">✓</span>
  ) : (
    <span className="font-mono text-neutral-300">✗</span>
  );
}

function RelativeDate({ iso }: { iso: string }) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  let label: string;
  if (diffMin < 1) label = 'just now';
  else if (diffMin < 60) label = `${diffMin} min ago`;
  else if (diffHr < 24) label = `${diffHr} hr ago`;
  else if (diffDay < 30) label = `${diffDay} days ago`;
  else if (diffDay < 365) label = `${Math.floor(diffDay / 30)} mo ago`;
  else label = `${Math.floor(diffDay / 365)} yr ago`;

  return <span title={d.toISOString().slice(0, 16).replace('T', ' ')}>{label}</span>;
}
